/**
 * t187u: one offline bar under the phone's top bar, on every tab: "Offline · 2 waiting", "Sending 2…" or
 * "1 not sent · tap to review", counting everything saved on the phone (clock punches, run stops, vehicle checks).
 * Tap it for Waiting to send: each item with its state, the reason and the fix when it was refused, Retry and Discard.
 */
import { createContext, useState } from 'react';
import clsx from 'clsx';
import { ChevronRight, Clock3, CloudOff, RotateCw, Trash2 } from 'lucide-react';
import { CLOCK_OP_LABEL, outboxBanner } from '@tradeoutlet/shared';
import { useRunOutboxOps, type ClockOutboxOp, type RegisterOutboxOp, type RunOutboxOp } from '@tradeoutlet/data';
import { fmt } from '@/lib/format';
import { runOutbox, useOnline } from '@/lib/runOutbox';
import { clockOutbox, useClockOps } from '@/lib/clockOutbox';
import { registerOutbox, useRegisterOps } from '@/lib/registerOutbox';
import { BottomSheet } from './BottomSheet';

/** True inside the phone shell, which shows this bar: pages then leave their own waiting banner out. */
export const ShellOfflineBar = createContext(false);

const stuck = (s: string) => s === 'failed' || s === 'conflict';

export function OfflineBar() {
  const runOps = useRunOutboxOps();
  const clockOps = useClockOps();
  const regOps = useRegisterOps();
  const online = useOnline();
  const [open, setOpen] = useState(false);
  const all = runOps.length + clockOps.length + regOps.length;
  const failed = [...runOps, ...clockOps, ...regOps].filter(o => stuck(o.state)).length;
  const text = all ? outboxBanner({ online, waiting: all - failed, failed }) : null;
  if (!text) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="m-offline-bar" aria-haspopup="dialog"
        className={clsx('flex min-h-9 w-full shrink-0 items-center gap-2 px-4 text-left text-sm font-medium', failed ? 'bg-rose-600 text-white' : online ? 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-100' : 'bg-slate-800 text-white')}>
        {online ? <Clock3 className="h-4 w-4 shrink-0" aria-hidden /> : <CloudOff className="h-4 w-4 shrink-0" aria-hidden />}
        <span className="flex-1">{text}</span>
        <ChevronRight className="h-4 w-4" aria-hidden />
      </button>
      <BottomSheet open={open} onClose={() => setOpen(false)} title="Waiting to send" testId="m-offline-sheet">
        <ul>
          {clockOps.map(o => <ClockOpRow key={o.id} o={o} />)}
          {runOps.map(o => <OutboxRow key={o.id} o={o} />)}
          {regOps.map(o => <RegisterOpRow key={o.id} o={o} />)}
        </ul>
      </BottomSheet>
    </>
  );
}

function ClockOpRow({ o }: { o: ClockOutboxOp }) {
  const bad = stuck(o.state);
  return (
    <li className="flex items-center gap-3 border-t border-slate-100 px-4 py-3 dark:border-slate-800" data-testid="m-clock-op" data-state={o.state}>
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{CLOCK_OP_LABEL[o.kind]} · {fmt.time(o.payload.device_time)}</span>
        {bad
          ? <span className="block text-xs text-rose-700 dark:text-rose-300" data-testid="m-clock-op-error">Not sent: {o.error ?? 'refused'}. {o.fix}</span>
          : <span className="block text-xs text-slate-500">{o.state === 'sending' ? 'Sending…' : o.attempts ? `Waiting for signal · tried ${o.attempts}×` : 'Waiting to send'}</span>}
      </span>
      {bad && <button type="button" aria-label="Discard" data-testid="m-clock-op-discard" onClick={() => void clockOutbox.discard(o.id)} className="flex h-11 w-11 items-center justify-center rounded-full text-slate-600 active:bg-slate-100 dark:active:bg-slate-800"><Trash2 className="h-5 w-5" /></button>}
      <button type="button" aria-label="Retry" onClick={() => void clockOutbox.retry(o.id)} className="flex h-11 w-11 items-center justify-center rounded-full text-slate-700 active:bg-slate-100 dark:text-slate-200 dark:active:bg-slate-800"><RotateCw className="h-5 w-5" /></button>
    </li>
  );
}

/** A run stop / vehicle check waiting to send (also the Runs page's own sheet). */
export function OutboxRow({ o }: { o: RunOutboxOp }) {
  const stuck = o.state === 'failed' || o.state === 'conflict';
  return (
    <li className="flex items-center gap-3 border-t border-slate-100 px-4 py-3 dark:border-slate-800" data-testid="m-run-outbox-op">
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{o.label ?? o.kind}</span>
        <span className={clsx('block text-xs', stuck ? 'text-rose-700 dark:text-rose-300' : 'text-slate-500')}>
          {stuck ? `Not sent: ${o.error ?? 'refused'}` : o.state === 'sending' ? 'Sending…' : o.attempts ? `Waiting for signal · tried ${o.attempts}×` : 'Waiting to send'}
          {' · '}{fmt.time(o.createdAt)}
        </span>
      </span>
      {stuck && <button type="button" aria-label="Discard" onClick={() => void runOutbox.discard(o.id)} className="flex h-11 w-11 items-center justify-center rounded-full text-slate-600 active:bg-slate-100 dark:active:bg-slate-800"><Trash2 className="h-5 w-5" /></button>}
      <button type="button" aria-label="Retry" onClick={() => void runOutbox.retry(o.id)} className="flex h-11 w-11 items-center justify-center rounded-full text-slate-700 active:bg-slate-100 dark:text-slate-200 dark:active:bg-slate-800"><RotateCw className="h-5 w-5" /></button>
    </li>
  );
}

/** t302u: register marks waiting to send. */
function RegisterOpRow({ o }: { o: RegisterOutboxOp }) {
  const bad = stuck(o.state);
  return (
    <li className="flex items-center gap-3 border-t border-slate-100 px-4 py-3 dark:border-slate-800" data-testid="m-register-op">
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{o.label}</span>
        <span className={clsx('block text-xs', bad ? 'text-rose-700 dark:text-rose-300' : 'text-slate-500')}>
          {bad ? `Not sent: ${o.error ?? 'refused'}` : o.state === 'sending' ? 'Sending…' : o.attempts ? `Waiting for signal · tried ${o.attempts}×` : 'Waiting to send'}{' · '}{fmt.time(o.createdAt)}
        </span>
      </span>
      {bad && <button type="button" aria-label="Discard" onClick={() => void registerOutbox.discard(o.id)} className="flex h-11 w-11 items-center justify-center rounded-full text-slate-600 active:bg-slate-100 dark:active:bg-slate-800"><Trash2 className="h-5 w-5" /></button>}
      <button type="button" aria-label="Retry" onClick={() => void registerOutbox.retry(o.id)} className="flex h-11 w-11 items-center justify-center rounded-full text-slate-700 active:bg-slate-100 dark:text-slate-200 dark:active:bg-slate-800"><RotateCw className="h-5 w-5" /></button>
    </li>
  );
}
